import { useCallback } from 'react';
import { JSONContent } from '@tiptap/core';

import { SubActivityEdit } from '../sub-activity/sub-activity-edit';
import {
  DISCUSSION_TYPENAME,
  DiscussionProperties,
  EditProps,
  RICHTEXT_SETTINGS,
} from './discussion.constants';
import ActivityBuilderRichtextToolbar from 'approot/activity-builder/activity-builder-richtext-toolbar';
import { useRichtext } from 'approot/shared/richtext/richtext.hooks';
import {
  InfoBoxEditSpeech,
  InfoBoxEditThought,
} from '../info-box/info-box-edit';
import {
  BORDER_STYLE_BOX,
  BORDER_STYLE_SPEECH,
  BORDER_STYLE_THOUGHT,
} from 'global/constants';
import { COLOUR_PURPLE } from 'approot/shared/colour/colour.constants';
import { useInputFieldState } from '../component.hooks';
import { useBatchComponentUpdateContext } from '../shared/batch-component-update.context';

const DiscussionEdit = (props: EditProps) => {
  const {
    id,
    sectionId,
    type,
    sort,
    properties,
    subActivityProperties,
    version,
  } = props;
  const { style, discussion } = properties;

  const { updateComponentProperties } = useBatchComponentUpdateContext();

  const handleUpdate = useCallback(
    (updatedProperties: DiscussionProperties) => {
      updateComponentProperties({
        input: {
          sectionId,
          componentId: id,
          componentData: {
            properties: updatedProperties,
          },
        },
        properties: updatedProperties,
        typename: DISCUSSION_TYPENAME,
      });
    },
    [id, sectionId, updateComponentProperties]
  );

  const { setEditorStateToUpdate } = useInputFieldState<JSONContent>({
    onChange: json =>
      handleUpdate({
        ...properties,
        discussion: {
          ...properties.discussion,
          instruction: json,
        },
      }),
  });

  const { editor, toolbarRef, showToolbar } = useRichtext({
    onChange: json => setEditorStateToUpdate(json),
    initialValue: discussion.instruction,
    settings: RICHTEXT_SETTINGS,
  });

  return (
    <SubActivityEdit
      id={id}
      component={{
        id,
        type,
        sort,
        properties,
        sectionId,
        language: null,
        version,
        __typename: DISCUSSION_TYPENAME,
      }}
      subActivityDetails={subActivityProperties}
    >
      {() => (
        <>
          <ActivityBuilderRichtextToolbar
            editor={editor}
            toolbarRef={toolbarRef}
            showToolbar={showToolbar}
            canChangeBlockType={false}
          />

          {editor &&
            (style?.border === BORDER_STYLE_SPEECH ||
              style?.border === BORDER_STYLE_BOX) && (
              <InfoBoxEditSpeech
                style={{ ...style, baseColour: COLOUR_PURPLE.base }}
                editor={editor}
                toolbarRef={toolbarRef}
              />
            )}

          {editor && style?.border === BORDER_STYLE_THOUGHT && (
            <InfoBoxEditThought
              style={{ ...style, baseColour: COLOUR_PURPLE.base }}
              editor={editor}
              toolbarRef={toolbarRef}
            />
          )}
        </>
      )}
    </SubActivityEdit>
  );
};

export default DiscussionEdit;
